"use client";

import Footer from "@/components/footer";
import Navbar from "@/components/navbar";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Home, Search } from "lucide-react";
import Link from "next/link";

export default function NotFound() {
  return (
    <>
      <Navbar />
      <main className="bg-background relative isolate flex min-h-svh w-full items-center justify-center overflow-hidden px-4 py-12 sm:px-6">
        {/* Fancy gradient background */}
        <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
          {/* Base gradient - diagonal from top-left to bottom-right */}
          <div
            className="absolute inset-0"
            style={{
              background: `
                radial-gradient(ellipse 100% 80% at top left, oklch(0.646 0.222 41.116 / 0.15), transparent 60%),
                radial-gradient(ellipse 100% 80% at bottom right, oklch(0.828 0.189 84.429 / 0.12), transparent 60%),
                radial-gradient(ellipse 80% 60% at center, oklch(0.6 0.118 184.704 / 0.1), transparent 70%),
                linear-gradient(to bottom right, oklch(0.6 0.118 184.704 / 0.08) 0%, transparent 40%, oklch(0.769 0.188 70.08 / 0.1) 100%),
                oklch(1 0 0)
              `,
            }}
          />

          {/* Animated floating orbs */}
          <div className="bg-chart-1/20 absolute top-20 -left-32 size-96 animate-pulse rounded-full blur-3xl motion-safe:animate-pulse" />
          <div className="bg-chart-4/20 absolute -right-32 bottom-20 size-80 animate-pulse rounded-full blur-3xl motion-safe:animate-pulse motion-safe:delay-1000" />
          <div className="bg-chart-2/15 absolute top-1/2 left-1/2 size-72 -translate-x-1/2 -translate-y-1/2 animate-pulse rounded-full blur-3xl motion-safe:animate-pulse motion-safe:delay-2000" />

          {/* Subtle mesh gradient overlay */}
          <div
            className="absolute inset-0 opacity-30"
            style={{
              background:
                "linear-gradient(to bottom right, transparent 0%, oklch(0.646 0.222 41.116 / 0.05) 30%, oklch(0.828 0.189 84.429 / 0.05) 70%, transparent 100%)",
            }}
          />
        </div>

        <div className="relative z-10 mx-auto w-full max-w-lg px-4 text-center sm:px-6">
          <div className="mb-4 flex justify-center sm:mb-6">
            <div className="bg-muted/50 text-muted-foreground relative flex size-24 items-center justify-center rounded-full backdrop-blur-sm sm:size-32 md:size-40">
              <span
                className="text-4xl font-bold sm:text-6xl md:text-7xl"
                style={{
                  backgroundImage:
                    "linear-gradient(to right, oklch(0.646 0.222 41.116), oklch(0.828 0.189 84.429))",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                404
              </span>
              <div className="bg-primary/20 text-primary border-background absolute -top-1 -right-1 flex size-8 items-center justify-center rounded-full border-2 backdrop-blur-sm sm:-top-2 sm:-right-2 sm:size-12 md:size-16">
                <Search className="size-4 sm:size-6 md:size-8" aria-hidden="true" />
              </div>
            </div>
          </div>
          <h1
            className="mb-2 text-xl font-semibold tracking-tight sm:text-2xl md:text-3xl"
            style={{
              backgroundImage:
                "linear-gradient(to right, oklch(0.145 0 0), oklch(0.646 0.222 41.116), oklch(0.828 0.189 84.429))",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}
          >
            Page not found
          </h1>
          <p className="text-muted-foreground mb-4 text-xs sm:mb-6 sm:text-sm md:text-base">
            The page you&apos;re looking for doesn&apos;t exist or has been moved. Let&apos;s get
            you back on track.
          </p>
          <div className="mb-6 flex flex-col items-stretch gap-3 sm:flex-row sm:items-center sm:justify-center">
            <Link
              href="/"
              className="focus-visible:ring-ring w-full focus-visible:rounded-sm focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none sm:w-auto"
            >
              <Button
                className="relative w-full overflow-hidden transition-all duration-300 hover:scale-105 sm:w-auto"
                style={{
                  background:
                    "linear-gradient(to right, oklch(0.646 0.222 41.116), oklch(0.828 0.189 84.429))",
                  color: "white",
                  border: "none",
                }}
              >
                <Home className="mr-2 size-4" />
                Go to home
              </Button>
            </Link>
            <Button
              onClick={() => window.history.back()}
              className="relative w-full overflow-hidden border-2 transition-all duration-300 hover:scale-105 sm:w-auto"
              variant="outline"
              style={{
                background:
                  "linear-gradient(to right, oklch(0.646 0.222 41.116 / 0.1), oklch(0.828 0.189 84.429 / 0.1))",
                borderImage:
                  "linear-gradient(to right, oklch(0.646 0.222 41.116), oklch(0.828 0.189 84.429)) 1",
                color: "oklch(0.646 0.222 41.116)",
              }}
            >
              <ArrowLeft className="mr-2 size-4" />
              Go back
            </Button>
          </div>
          <div className="mt-6 text-center sm:mt-8">
            <p className="text-muted-foreground mb-3 text-xs font-medium sm:text-sm">
              Popular pages:
            </p>
            <nav className="flex flex-wrap justify-center gap-2 sm:gap-3">
              {[
                { href: "/#portfolio", label: "Portfolio" },
                { href: "/#services", label: "Services" },
                { href: "/#about", label: "About" },
                { href: "/#contact", label: "Contact" },
              ].map(link => (
                <Link
                  key={link.href}
                  href={link.href}
                  className="text-primary hover:text-primary/80 focus-visible:ring-ring text-xs transition-colors duration-300 hover:underline focus-visible:rounded-sm focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none sm:text-sm"
                  style={{
                    backgroundImage:
                      "linear-gradient(to right, oklch(0.646 0.222 41.116), oklch(0.828 0.189 84.429))",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                  }}
                >
                  {link.label}
                </Link>
              ))}
            </nav>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}
